import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `adminBilling:` scope (F70), following the
 * `shares:`/`search:` precedent — see `apps/web/src/routes/shares/shares.i18n.ts`.
 * NOT a second i18n runtime (E18/§13.3 ban); `packages/i18n/**` is outside
 * this task's ownership.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/admin.json`.
 */
const en = {
  'page.title': 'Billing',
  'page.description': 'Platform billing health — revenue, subscriptions, invoices, and Paddle reconciliation.',
  'overview.error.title': "Couldn't load the billing overview.",
  'overview.generatedAt': 'As of {time}',
  'stat.mrr': 'MRR',
  'stat.activeSubscriptions': 'Active subscriptions',
  'stat.churnRate': 'Churn rate',
  'reconciliation.title': 'Subscriptions — Paddle reconciliation',
  'reconciliation.description': 'The most recent local vs. Paddle mismatches (up to 20). A full unfiltered subscriptions roster isn’t available yet — see this task’s returned open-issues list.',
  'reconciliation.empty': 'No mismatches — local records agree with Paddle.',
  'reconciliation.column.user': 'User',
  'reconciliation.column.localStatus': 'Local status',
  'reconciliation.column.paddleStatus': 'Paddle status',
  'reconciliation.column.detectedAt': 'Detected',
  'subscriptionStatus.active': 'Active',
  'subscriptionStatus.trialing': 'Trialing',
  'subscriptionStatus.past_due': 'Past due',
  'subscriptionStatus.paused': 'Paused',
  'subscriptionStatus.canceled': 'Canceled',
  'subscriptionStatus.none': '—',
  'invoices.title': 'Invoices',
  'invoices.list.column.issuedAt': 'Issued',
  'invoices.list.column.amount': 'Amount',
  'invoices.list.column.status': 'Status',
  'invoices.list.column.paddleTransactionId': 'Paddle transaction',
  'invoices.list.empty.title': 'No invoices yet',
  'invoices.list.empty.description': 'Invoices will appear here once purchases start coming in.',
  'invoices.list.error.title': "Couldn't load invoices.",
  'invoices.list.noResults.title': 'No invoices match “{query}”',
  'invoices.list.searchLabel': 'Search invoices',
  'invoices.list.searchPlaceholder': 'Search invoices…',
  'invoiceStatus.paid': 'Paid',
  'invoiceStatus.open': 'Open',
  'invoiceStatus.void': 'Void',
  'invoiceStatus.refunded': 'Refunded',
} as const;

const ro = {
  'page.title': 'Facturare',
  'page.description': 'Starea facturării platformei — venituri, abonamente, facturi și reconciliere Paddle.',
  'overview.error.title': 'Nu am putut încărca imaginea de ansamblu a facturării.',
  'overview.generatedAt': 'La data de {time}',
  'stat.mrr': 'MRR',
  'stat.activeSubscriptions': 'Abonamente active',
  'stat.churnRate': 'Rată de abandon',
  'reconciliation.title': 'Abonamente — reconciliere Paddle',
  'reconciliation.description': 'Cele mai recente nepotriviri local vs. Paddle (până la 20). O listă completă, nefiltrată, a abonamentelor nu este încă disponibilă — vezi lista de probleme deschise a acestei sarcini.',
  'reconciliation.empty': 'Nicio nepotrivire — înregistrările locale corespund cu Paddle.',
  'reconciliation.column.user': 'Utilizator',
  'reconciliation.column.localStatus': 'Stare locală',
  'reconciliation.column.paddleStatus': 'Stare Paddle',
  'reconciliation.column.detectedAt': 'Detectat',
  'subscriptionStatus.active': 'Activ',
  'subscriptionStatus.trialing': 'Perioadă de probă',
  'subscriptionStatus.past_due': 'Restanță',
  'subscriptionStatus.paused': 'Pauzat',
  'subscriptionStatus.canceled': 'Anulat',
  'subscriptionStatus.none': '—',
  'invoices.title': 'Facturi',
  'invoices.list.column.issuedAt': 'Emisă',
  'invoices.list.column.amount': 'Sumă',
  'invoices.list.column.status': 'Stare',
  'invoices.list.column.paddleTransactionId': 'Tranzacție Paddle',
  'invoices.list.empty.title': 'Nicio factură încă',
  'invoices.list.empty.description': 'Facturile vor apărea aici pe măsură ce apar achiziții.',
  'invoices.list.error.title': 'Nu am putut încărca facturile.',
  'invoices.list.noResults.title': 'Nicio factură nu se potrivește cu „{query}”',
  'invoices.list.searchLabel': 'Caută facturi',
  'invoices.list.searchPlaceholder': 'Caută facturi…',
  'invoiceStatus.paid': 'Plătită',
  'invoiceStatus.open': 'Deschisă',
  'invoiceStatus.void': 'Anulată',
  'invoiceStatus.refunded': 'Rambursată',
} as const;

export const ADMIN_BILLING_CATALOG: Record<Locale, Record<AdminBillingCatalogKey, string>> = { en, ro };
export type AdminBillingCatalogKey = keyof typeof en;
